<template>
   <div class="select-subscription-container">
      <div class="pricing-card">
         <div class="header">
            <h1>Choose Your Plan</h1>
            <p>Select a subscription tier to start your free trial.</p>
         </div>

         <div class="pricing-tiers">
            <div
               class="tier-card starter"
               :class="{ selected: selectedTier === 'starter_tier' }"
            >
               <div class="tier-header">
                  <h3>Starter</h3>
                  <div class="price">
                     <span class="amount">$199/month</span>
                     <span class="trial">30-day trial</span>
                     <span class="visitors">Up to 35K visitors/month</span>
                  </div>
               </div>
               <div class="tier-features">
                  <ul>
                     <li>✓ No-code Visual Editor</li>
                     <li>✓ Performance Dashboard</li>
                     <li>✓ Guided Setup</li>
                     <li>✓ Email Support</li>
                     <li>✓ 1 Project</li>
                     <li>✓ 10 Variables</li>
                     <li>✓ 1 Seat</li>
                     <li>✓ $0.01/prediction overage</li>
                  </ul>
               </div>
               <QBtn
                  id="starter-tier-button"
                  color="primary"
                  size="lg"
                  :outline="selectedTier !== 'starter_tier'"
                  label="Start Trial"
                  class="tier-button"
                  :loading="loading && selectedTier === 'starter_tier'"
                  @click="selectTier('starter_tier')"
               />
            </div>

            <div
               class="tier-card professional"
               :class="{ selected: selectedTier === 'professional_tier' }"
            >
               <div class="tier-header">
                  <h3>Professional</h3>
                  <div class="price">
                     <span class="amount">$1,999/month</span>
                     <span class="trial">14-day trial</span>
                     <span class="visitors">Up to 500K visitors/month</span>
                  </div>
               </div>
               <div class="tier-features">
                  <p class="text-left">All Starter tier features, plus:</p>
                  <ul>
                     <li>✓ Deeper Analytics</li>
                     <li>✓ Beta Feature Access</li>
                     <li>✓ ezbot Slack access</li>
                     <li>✓ Priority Support</li>
                     <li>✓ 3 Projects</li>
                     <li>✓ Unlimited Variables</li>
                     <li>✓ 3 Seats</li>
                     <li>✓ $0.008/prediction overage</li>
                  </ul>
               </div>
               <QBtn
                  id="professional-tier-button"
                  color="primary"
                  size="lg"
                  :outline="selectedTier !== 'professional_tier'"
                  label="Start Trial"
                  class="tier-button"
                  :loading="loading && selectedTier === 'professional_tier'"
                  @click="selectTier('professional_tier')"
               />
            </div>
         </div>

         <div class="footer">
            <p class="help-text">
               Need help choosing? Contact us at
               <a href="mailto:support@ezbot.ai">support@ezbot.ai</a>
            </p>
         </div>
      </div>
   </div>
</template>

<script setup lang="ts">
import { ref } from "vue"
import { useQuasar } from "quasar"
import { useOnboardingStore } from "@/stores/onboardingStore"
import { createStripeCheckoutSessionForTier } from "@/services/adminBackendClient"
import { callWithLoading } from "@/utils/callWithLoading"
import { collectError } from "@/utils/errors"
import { toast } from "vue3-toastify"

const $q = useQuasar()
const onboardingStore = useOnboardingStore()

const selectedTier = ref<string | null>(null)
const loading = ref(false)

const selectTier = async (tier: "starter_tier" | "professional_tier") => {
   selectedTier.value = tier
   loading.value = true

   try {
      // Set tier in store
      onboardingStore.setTier(tier)

      // Create checkout session
      const response = await callWithLoading(
         () => createStripeCheckoutSessionForTier(tier),
         $q,
      )

      onboardingStore.setCheckoutSession(response.data)
      window.location.href = response.data.url
   } catch (error) {
      collectError(error as Error)
      toast.error("Failed to create checkout session. Please try again.")
   } finally {
      loading.value = false
      selectedTier.value = null
   }
}
</script>

<style lang="scss" scoped>
.select-subscription-container {
   min-height: 100vh;
   background-image: linear-gradient(to top right, #007bff, #00aaff);
   display: flex;
   justify-content: center;
   align-items: center;
   padding: 20px;
}

.pricing-card {
   background: white;
   border-radius: 12px;
   box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
   max-width: 900px;
   width: 100%;
   padding: 40px;
}

.header {
   text-align: center;
   margin-bottom: 40px;

   h1 {
      margin: 0 0 16px 0;
      color: #2c3e50;
      font-size: 32px;
      font-weight: 700;
   }

   p {
      margin: 0;
      color: #7f8c8d;
      font-size: 18px;
      line-height: 1.5;
   }
}

.pricing-tiers {
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 30px;
   margin-bottom: 40px;

   @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 20px;
   }
}

.tier-card {
   border: 2px solid #e1e8ed;
   border-radius: 8px;
   padding: 30px;
   text-align: center;
   transition: all 0.3s ease;
   display: flex;
   flex-direction: column;
   height: 100%;

   &:hover {
      border-color: #1976d2;
      transform: translateY(-2px);
      box-shadow: 0 4px 20px rgba(25, 118, 210, 0.15);
   }

   &.selected {
      border-color: #1976d2;
      background-color: #f8fbff;
   }
}

.tier-header {
   margin-bottom: 24px;

   h3 {
      margin: 0 0 12px 0;
      color: #2c3e50;
      font-size: 24px;
      font-weight: 600;
   }

   .amount {
      font-size: 24px;
      color: #2c3e50;
      font-weight: 700;
      display: block;
      margin-bottom: 8px;
   }

   .trial {
      font-size: 14px;
      color: #27ae60;
      font-weight: 600;
      background: #d5f4e6;
      padding: 4px 10px;
      border-radius: 20px;
      display: inline-block;
      margin-bottom: 4px;
   }

   .visitors {
      font-size: 14px;
      color: #7f8c8d;
      font-weight: 500;
      display: block;
   }
}

.tier-features {
   flex-grow: 1;
   margin-bottom: 30px;

   ul {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
         padding: 8px 0;
         color: #34495e;
         font-size: 14px;
         text-align: left;
         line-height: 1.4;
      }
   }
}

.tier-button {
   width: 100%;
   font-weight: 600;
   padding: 12px 0;
   margin-top: auto;
}

.footer {
   text-align: center;
   padding-top: 30px;
   border-top: 1px solid #e1e8ed;

   .help-text {
      margin: 0;
      color: #7f8c8d;
      font-size: 14px;

      a {
         color: #1976d2;
         text-decoration: none;

         &:hover {
            text-decoration: underline;
         }
      }
   }
}
</style>
